import { FC, useCallback, useEffect, useRef } from 'react';

import { useRecoilState } from 'recoil';

import ZoomPhoneSmartEmbed, {
  ZoomPhoneSmartEmbedEvent
} from 'components/ZoomPhoneSmartEmbed/ZoomPhoneSmartEmbed';
import { SalesforceCallService } from 'constants/salesforceCall';
import {
  useLogSalesforceCall,
  useUpdateSalesforceCallNotes
} from 'services/hooks/SalesforceAccounts.hooks';
import { zoomPhoneSessionAtom } from 'store/atoms/zoomPhoneAtom';
import { UpdateSalesforceCallNotesResponse } from 'types/SalesforceAccounts.types';
import { translate } from 'utils';
import { showToast } from 'utils/toast/toast.util';

/** Zoom emits callId first on ringing; later events used as failsafe backups. */
const ZOOM_CALL_ID_EVENTS = new Set([
  'zp-call-ringing-event',
  'zp-call-connected-event',
  'zp-call-ended-event'
]);

const extractZoomCallId = (
  event: ZoomPhoneSmartEmbedEvent
): string | undefined => {
  const data = event.data;
  if (!data || typeof data !== 'object') return undefined;

  const callId = (data as { callId?: unknown }).callId;
  if (typeof callId !== 'string') return undefined;

  const trimmed = callId.trim();
  return trimmed || undefined;
};

const extractZoomNotesData = (
  event: ZoomPhoneSmartEmbedEvent
): Record<string, unknown> | undefined => {
  const data = event.data;
  if (!data || typeof data !== 'object') return undefined;

  const notesData = (data as { notesData?: unknown }).notesData;
  if (!notesData || typeof notesData !== 'object' || Array.isArray(notesData)) {
    return undefined;
  }

  return notesData as Record<string, unknown>;
};

const showCallNotesUpdateFailedToast = () => {
  showToast(
    translate('salesforce.accounts.sidepanel.activity_update_call_notes_error'),
    false
  );
};

/**
 * App-shell host for Zoom Phone Smart Embed.
 * Keeps the session alive across route changes via Recoil.
 */
const ZoomPhoneSmartEmbedHost: FC = () => {
  const [session, setSession] = useRecoilState(zoomPhoneSessionAtom);
  const relatedIdRef = useRef(session?.relatedId ?? '');
  relatedIdRef.current = session?.relatedId ?? '';

  /** callIds already sent (or in flight) — cleared when the embed closes or session is replaced. */
  const registeredCallIdsRef = useRef(new Set<string>());
  /** Notes-save event ids already processed — cleared when the embed closes or session is replaced. */
  const savedNotesEventIdsRef = useRef(new Set<string>());
  const sessionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!session?.open) {
      sessionKeyRef.current = null;
      return;
    }

    const nextKey = `${session.relatedId}:${session.phoneNumber}`;
    if (sessionKeyRef.current && sessionKeyRef.current !== nextKey) {
      registeredCallIdsRef.current.clear();
      savedNotesEventIdsRef.current.clear();
    }
    sessionKeyRef.current = nextKey;
  }, [session?.open, session?.relatedId, session?.phoneNumber]);

  const [registerZoomCallId] = useLogSalesforceCall();
  const [updateSalesforceCallNotes] = useUpdateSalesforceCallNotes();

  const cacheCallIdOnBackend = useCallback(
    (callId: string) => {
      const recordId = relatedIdRef.current;
      if (!recordId || registeredCallIdsRef.current.has(callId)) return;

      registeredCallIdsRef.current.add(callId);

      registerZoomCallId({
        variables: {
          input: {
            callId,
            relatedId: recordId,
            service: SalesforceCallService.ZOOM
          }
        }
      }).catch(() => {
        // Allow a later Zoom event (connected / ended) to retry.
        registeredCallIdsRef.current.delete(callId);
      });
    },
    [registerZoomCallId]
  );

  const saveZoomCallNotes = useCallback(
    (event: ZoomPhoneSmartEmbedEvent) => {
      const eventKey = event.id?.trim() || extractZoomCallId(event);
      if (eventKey && savedNotesEventIdsRef.current.has(eventKey)) return;
      if (eventKey) savedNotesEventIdsRef.current.add(eventKey);

      const callId = extractZoomCallId(event);
      const notesData = extractZoomNotesData(event);
      if (!callId || !notesData) {
        showCallNotesUpdateFailedToast();
        if (eventKey) savedNotesEventIdsRef.current.delete(eventKey);
        return;
      }

      updateSalesforceCallNotes({
        variables: {
          input: {
            callId,
            notesData
          }
        }
      })
        .then((result: { data?: UpdateSalesforceCallNotesResponse | null }) => {
          if (!result.data?.updateSalesforceCallNotes?.success) {
            showCallNotesUpdateFailedToast();
            if (eventKey) savedNotesEventIdsRef.current.delete(eventKey);
          }
        })
        .catch(() => {
          showCallNotesUpdateFailedToast();
          if (eventKey) savedNotesEventIdsRef.current.delete(eventKey);
        });
    },
    [updateSalesforceCallNotes]
  );

  const handleZoomEvent = useCallback(
    (event: ZoomPhoneSmartEmbedEvent) => {
      if (event.type === 'zp-notes-save-event') {
        saveZoomCallNotes(event);
        return;
      }

      if (!ZOOM_CALL_ID_EVENTS.has(event.type)) return;

      const callId = extractZoomCallId(event);
      if (callId) cacheCallIdOnBackend(callId);
    },
    [cacheCallIdOnBackend, saveZoomCallNotes]
  );

  const handleClose = useCallback(() => {
    setSession(null);
    registeredCallIdsRef.current.clear();
    savedNotesEventIdsRef.current.clear();
  }, [setSession]);

  if (!session?.open) return null;

  return (
    <ZoomPhoneSmartEmbed
      open={session.open}
      phoneNumber={session.phoneNumber}
      relatedId={session.relatedId}
      autoDial={session.autoDial ?? false}
      onClose={handleClose}
      onEvent={handleZoomEvent}
    />
  );
};

export default ZoomPhoneSmartEmbedHost;
